import { nextMessageId, normalizeMessageId } from '../chat/messageId.ts'
import { resolveMessageTimestamp } from '../chat/messageTime.ts'
import { getMessageTimerTtl } from '../chat/messageTimer.ts'
import { resolveIncomingExpiry, ttlMsToDurationSeconds, wrapWithViewTtl } from '../chat/zionExpiry.ts'
import {
  buildChatClearNotify,
  buildMessageDeleteNotify,
  parseChatClearNotify,
  parseMessageDeleteNotify,
} from '../chat/messageDeleteControl.ts'
import {
  buildMsgReadAck,
  buildMsgDeliverAck,
  isZionControlPlain,
  parseMsgReadAck,
  parseMsgDeliverAck,
  parseMsgResyncRequest,
  parseMsgKeyOffer,
  parseMsgViewed,
} from '../chat/zionControl.ts'
import {
  applyIncomingDeliverAck,
  armDeliverAckWaiter,
  clearAllDeliverAckWaiters,
  clearAllOutboundRetries,
  scheduleOutboundRetry,
} from '../chat/messageDelivery.ts'
import {
  applyPeerKeyOffer,
  bindSessionHealHandlers,
  onDecryptFailure,
  respondToResyncRequest,
  retryAllPendingDecrypts,
} from '../chat/sessionHeal.ts'
import type { ReplyTarget } from '../chat/messageReplyControl.ts'
import { encryptTextMessage, encryptControlMessage, encryptMediaMessage, tryDecryptFromAllContacts } from '../crypto/zionPrivacyCrypto.ts'
import {
  BLOCKED_PLACEHOLDER,
  DOCUMENT_TOO_LARGE_MSG,
  GIF_TOO_LARGE_MSG,
  documentDisplayLabel,
  isGifBytes,
  isMediaKind,
  KIND_AUDIO,
  KIND_FILE,
  KIND_GIF,
  kindFromMime,
  maxBytesForKind,
  MAX_DOCUMENT_BYTES,
  MAX_GIF_BYTES,
  normalizeKind,
} from '../media/zionMediaProtocol.ts'
import { putMediaBytes, deleteMediaBytes } from '../media/mediaCache.ts'
import { gzipCompressIfSmaller } from '../media/documentGzip.ts'
import { sanitizeAudioBytes, sanitizeMediaFile } from '../media/mediaSanitizer.ts'
import { encodeVoiceForAndroid } from '../media/voiceEncode.ts'
import { getAutoMediaDownload } from '../storage/privacySettingsStore.ts'
import {
  ensureInboundQueue,
  getBaseNickname,
  getCurrentNickname,
  registerDailyCodeForSession,
  ensureFreshDailyCode,
} from '../identity/ZionIdentityManager.ts'
import { getPrivateKey, getPublicKeyBase64, ensureKeyPair } from '../storage/keyStore.ts'
import {
  appendMessage,
  deleteMessage,
  patchMessage,
  deleteMessagesForContact,
  getMessageById,
  loadMessages,
  messageExists,
  type ChatMessage,
} from '../storage/chatStore.ts'
import {
  contactDisplayName,
  findContact,
  findContactBySession,
  listContacts,
  removeContactCompletely,
  restorePairedContactsFromMirror,
  setPendingPairingRequest,
  type PairingRequest,
  type ZionContact,
} from '../storage/pairingStore.ts'
import {
  getDailyCode,
  getInboundQueueId,
  getSessionToken,
  saveInboundQueueId,
  saveQueueOwnerPubKey,
  saveSessionToken,
} from '../storage/sessionStore.ts'
import {
  broadcastQueueUpdate,
  completePairingFromAccept,
  connectSocket,
  createSession,
  ensureSocketReady,
  acceptPendingPairing,
  pollInboxBurst,
  pullBlobs,
  pullSessionBlobs,
  pushBlobWithResult,
  relayPrivateBlob,
  sendChatDeleted,
  setBlobHandler,
  setMessageEchoHandler,
  setPairingHandlers,
  setHistoryPushHandler,
  syncInbox,
  syncMediaPrefs,
  unwatchQueue,
  watchQueue,
} from '../socket2/ZionSocketClient.ts'
import { applyIncomingHistoryPush } from '../deviceLink/deviceLinkService.ts'
import {
  hideSyncOverlay,
  showSyncOverlay,
  updateSyncOverlay,
} from '../deviceLink/syncOverlay.ts'
import { notifyIncomingMessage } from '../notifications/messageNotify.ts'
import { playInChatIncomingTone } from '../notifications/audioUnlock.ts'
import { clearUnread, clearUnreadForChat, incrementUnread, syncAppBadge } from '../storage/unreadStore.ts'
import {
  acceptTempChatInvite,
  bindTempChatContext,
  cancelTempChatOutgoing,
  endTempChatEarly,
  formatTempRemaining,
  getTempChatByKey,
  installTempChatHandlers,
  listPendingIncomingTempChats,
  listTempChats,
  rejectTempChatInvite,
  startTempChatWithContact,
} from '../tempChat/tempChatService.ts'
import { notifyPairingRequest } from '../notifications/pairingNotify.ts'
import { buildNativeApnsPayload, wakePeerBySession } from '../push/pushClient.ts'
import {
  syncPushRegistration,
  ensurePwaPushOnLaunch,
  startPushPresenceHeartbeat,
  installPushLifecycleHooks,
} from '../push/webPush.ts'
import { navigate } from '../ui/router.ts'
import {
  ensureContactWithPublicKey,
  hydrateAllContactsPublicKeys,
} from '../contacts/contactKeyService.ts'
import { clearActiveChatOnSw, syncActiveChatToSw } from './activeChatBridge.ts'
import { silentReHandshakeToContact } from '../pairing/silentReHandshake.ts'

const queueToContact = new Map<string, string>()
const seenBlobs = new Set<string>()
let syncTimer: number | null = null
let lifecycleHooksInstalled = false
let currentChatContactId = ''
let currentTempChatKey = ''

export async function ensurePrivacySession(): Promise<string> {
  let token = (await getSessionToken())?.trim() ?? ''

  if (!token) {
    // Solo primo avvio: crea sessione. Mai remintare se esiste già in IndexedDB.
    try {
      token = (await createSession(null)).trim()
    } catch (err) {
      throw err instanceof Error ? err : new Error('session_create_failed')
    }
    if (!token) throw new Error('empty_session_token')
    await saveSessionToken(token)
  } else {
    // Soft-renew sul server (stesso token se ancora in RAM/disco).
    // Errori di rete / 5xx → tieni il token locale, zero wipe.
    try {
      const renewed = (await createSession(token)).trim()
      if (renewed && renewed !== token) {
        await saveSessionToken(renewed)
        await saveInboundQueueId('')
        await saveQueueOwnerPubKey('')
        token = renewed
      }
    } catch {
      /* offline / temporaneo — riusa credenziali IndexedDB */
    }
  }

  try {
    const { queueId, recreated } = await ensureInboundQueue()
    if (recreated) {
      await notifyPeersOfQueueUpdate(token, queueId)
    }
  } catch {
    /* coda locale resta; retry al prossimo bootstrap */
  }

  try {
    await ensureFreshDailyCode()
  } catch {
    try {
      await registerDailyCodeForSession()
    } catch {
      /* non cancellare sessione per errori discovery temporanei */
    }
  }

  const myQueue = await getInboundQueueId()
  if (myQueue) {
    watchQueue(myQueue, token)
    queueToContact.set(myQueue, '__self__')
  }

  try {
    const { persistSessionForServiceWorker } = await import('../storage/sessionDbForSw.ts')
    const { persistContactsForServiceWorker } = await import('../storage/contactsDbForSw.ts')
    await persistContactsForServiceWorker()
    const snap = await persistSessionForServiceWorker()
    if (snap && navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SET_SESSION_SNAPSHOT',
        snapshot: snap,
      })
    }
  } catch {
    /* SW sync best-effort */
  }

  return token
}

async function notifyPeersOfQueueUpdate(session: string, queueId: string): Promise<void> {
  const contacts = await listContacts()
  if (!contacts.length) return
  await broadcastQueueUpdate(
    session,
    queueId,
    contacts.map((c) => c.sessionToken),
  )
}

async function handleIncomingBlob(
  _queueId: string,
  blob: string,
  opts: { forceOutgoing?: boolean; hintMessageId?: string; hintPeerSession?: string } = {},
): Promise<boolean> {
  if (seenBlobs.has(blob)) return true

  const privateKey = await getPrivateKey()
  const contacts = await listContacts()
  if (!contacts.length) {
    await onDecryptFailure(blob, opts)
    return false
  }

  const mySession = (await getSessionToken())?.trim() ?? ''

  let contact: ZionContact | undefined
  const peerSession = opts.hintPeerSession?.trim()
  if (peerSession) {
    contact = (await findContactBySession(peerSession)) ?? undefined
  }

  let match: Awaited<ReturnType<typeof tryDecryptFromAllContacts>> | null = null
  if (contact) {
    try {
      const { decryptForContact } = await import('../crypto/ZionCipher.ts')
      const decrypted = await decryptForContact(privateKey, blob, contact.publicKey)
      const { extractPlainText } = await import('../crypto/zionPrivacyCrypto.ts')
      const parsed = extractPlainText(decrypted)
      if (parsed) {
        match = {
          index: contacts.findIndex((c) => c.contactId === contact!.contactId),
          text: parsed.text,
          senderId: parsed.senderId,
          messageId: parsed.messageId,
          timestamp: parsed.timestamp,
          replyTo: parsed.replyTo,
          contentKind: parsed.contentKind,
          mediaB64: parsed.mediaB64,
          mime: parsed.mime,
          fileName: parsed.fileName,
          mediaGzip: parsed.mediaGzip,
          mediaOrigSize: parsed.mediaOrigSize,
          durationMs: parsed.durationMs,
        }
      }
    } catch {
      /* try all contacts */
    }
  }

  if (!match) {
    match = await tryDecryptFromAllContacts(privateKey, blob, contacts)
  }

  if (!match) {
    // NON scartare: pending + auto-resync chiavi
    await onDecryptFailure(blob, opts)
    return false
  }

  // Solo dopo decrypt OK: dedup permanente
  seenBlobs.add(blob)
  if (seenBlobs.size > 400) {
    const drop = seenBlobs.values().next().value
    if (drop) seenBlobs.delete(drop)
  }

  if (match.index >= 0 && contacts[match.index]) {
    contact = contacts[match.index]!
  }

  if (match.senderId) {
    const bySender = await findContactBySession(match.senderId)
    if (bySender && !opts.forceOutgoing) contact = bySender
  }

  if (!contact) return false

  const isOwnEcho =
    opts.forceOutgoing ||
    (!!mySession && !!match.senderId && match.senderId.trim() === mySession)

  const plain = match.text
  const hasMedia = isMediaKind(match.contentKind) && !!match.mediaB64

  if (parseChatClearNotify(plain)) {
    if (isOwnEcho) await purgeContactChat(contact.contactId)
    return true
  }

  const deliverAck = parseMsgDeliverAck(plain)
  if (deliverAck) {
    if (!isOwnEcho) await applyIncomingDeliverAck(deliverAck.messageId)
    return true
  }

  const resyncReq = parseMsgResyncRequest(plain)
  if (resyncReq) {
    if (!isOwnEcho) await respondToResyncRequest(contact)
    return true
  }
  const keyOffer = parseMsgKeyOffer(plain)
  if (keyOffer) {
    if (!isOwnEcho) await applyPeerKeyOffer(contact, keyOffer.publicKey)
    return true
  }

  const readAck = parseMsgReadAck(plain)
  if (readAck) {
    if (!isOwnEcho) await applyIncomingMsgReadAck(contact.contactId, readAck.messageId)
    return true
  }
  if (parseMsgViewed(plain)) return true
  if (isZionControlPlain(plain) && !hasMedia) return true

  const { processTempChatControlFromPlain, routePlainToActiveTempChat } = await import(
    '../tempChat/tempChatService.ts'
  )
  if (processTempChatControlFromPlain(plain, contact)) return true

  const msgId = normalizeMessageId(opts.hintMessageId || match.messageId, Date.now())
  if (await messageExists(msgId)) {
    if (!isOwnEcho) {
      void sendControlToContact(contact.contactId, buildMsgDeliverAck(msgId))
    }
    return true
  }

  if (!isOwnEcho && !hasMedia) {
    if (await routePlainToActiveTempChat(contact, plain, msgId)) return true
  }

  const deletedId = parseMessageDeleteNotify(plain)
  if (deletedId) {
    await deleteMessage(deletedId)
    return true
  }

  let text = plain
  let mediaKind: string | undefined
  let mediaMime: string | undefined
  let mediaFileName: string | undefined
  let mediaGzip: boolean | undefined
  let mediaOrigSize: number | undefined
  let audioDurationMs: number | undefined
  let viewTtlMs: number | undefined

  const expiry = resolveIncomingExpiry(plain)
  if (expiry) {
    text = expiry.body
    if (expiry.viewTtlMs && expiry.viewTtlMs > 0) viewTtlMs = expiry.viewTtlMs
  } else {
    // TTL assoluto scaduto: ACK comunque così il mittente non ritenta all'infinito
    if (!isOwnEcho) {
      void sendControlToContact(contact.contactId, buildMsgDeliverAck(msgId))
    }
    return true
  }

  if (hasMedia) {
    if (!getAutoMediaDownload()) {
      text = BLOCKED_PLACEHOLDER
    } else {
      try {
        const raw = Uint8Array.from(atob(match.mediaB64!), (c) => c.charCodeAt(0))
        let kind = normalizeKind(match.contentKind)
        const mimeHint = (match.mime || '').trim().toLowerCase()
        if (
          kind !== KIND_GIF &&
          (mimeHint === 'image/gif' || mimeHint.includes('gif') || isGifBytes(raw))
        ) {
          kind = KIND_GIF
        }
        const max = maxBytesForKind(kind)
        if (raw.length > 0 && raw.length <= max) {
          mediaKind = kind
          mediaMime = kind === KIND_GIF ? 'image/gif' : match.mime || 'application/octet-stream'
          mediaFileName = match.fileName
          mediaGzip = match.mediaGzip
          mediaOrigSize = match.mediaOrigSize ?? (mediaKind === KIND_FILE ? raw.length : undefined)
          if (typeof match.durationMs === 'number' && match.durationMs > 0) {
            audioDurationMs = Math.round(match.durationMs)
          }
          await putMediaBytes(contact.contactId, msgId, raw, mediaMime)
          if (mediaKind === KIND_FILE && !plain.trim() && mediaFileName) {
            const size = mediaOrigSize ?? raw.length
            text = documentDisplayLabel(mediaFileName, size)
          }
        } else {
          text = '[Media troppo grande o non valido]'
        }
      } catch {
        text = '[Media non decodificabile]'
      }
    }
  }

  const msg: ChatMessage = {
    id: msgId,
    contactId: contact.contactId,
    text,
    outgoing: isOwnEcho,
    timestamp: resolveMessageTimestamp({
      envelopeTs: match.timestamp,
      messageId: msgId,
    }),
    pending: false,
    replyToId: match.replyTo?.replyToId,
    replyPreview: match.replyTo?.replyPreview,
    mediaKind,
    mediaMime,
    mediaFileName,
    mediaGzip,
    mediaOrigSize,
    audioDurationMs,
    viewTtlMs,
    durationSeconds: viewTtlMs ? ttlMsToDurationSeconds(viewTtlMs) : undefined,
  }
  await appendMessage(msg)

  if (!isOwnEcho) {
    void sendControlToContact(contact.contactId, buildMsgDeliverAck(msgId))
    const viewingThisChat =
      document.visibilityState === 'visible' && getCurrentChatContactId() === contact.contactId
    if (!viewingThisChat) incrementUnread(contact.contactId)
    if (viewingThisChat) {
      playInChatIncomingTone()
    } else {
      const notifyContact =
        (await findContactBySession(match.senderId?.trim() || contact.sessionToken)) ??
        (await findContact(contact.contactId)) ??
        contact
      void notifyIncomingMessage(
        notifyContact,
        hasMedia ? mediaFileName || 'Media' : plain,
        msgId,
      )
    }
  }
  return true
}

function extractMessageEchoBlob(data: Record<string, unknown>): string {
  const direct = String(data.blob ?? data.message ?? data.encrypted ?? data.payload ?? '').trim()
  if (direct) return direct
  const nested = data.echoedMessage ?? data.echo
  if (nested && typeof nested === 'object') {
    const inner = nested as Record<string, unknown>
    return String(inner.blob ?? inner.message ?? inner.encrypted ?? inner.payload ?? '').trim()
  }
  return ''
}

function extractMessageEchoMeta(data: Record<string, unknown>): {
  messageId: string
  peerSession: string
} {
  const nested =
    data.echoedMessage && typeof data.echoedMessage === 'object'
      ? (data.echoedMessage as Record<string, unknown>)
      : data
  const rawId = nested.messageId ?? nested.message_id ?? data.messageId ?? data.message_id
  const messageId =
    rawId != null && String(rawId).trim() ? normalizeMessageId(rawId, Date.now()) : ''
  return {
    messageId,
    peerSession: String(
      nested.peerSessionToken ??
        nested.toSessionToken ??
        nested.contactSessionToken ??
        nested.peerSession ??
        data.peerSessionToken ??
        data.toSessionToken ??
        data.contactSessionToken ??
        '',
    ).trim(),
  }
}

async function handleMessageEcho(data: Record<string, unknown>): Promise<void> {
  const blob = extractMessageEchoBlob(data)
  if (!blob) return
  const { messageId, peerSession } = extractMessageEchoMeta(data)
  await handleIncomingBlob('', blob, {
    forceOutgoing: true,
    hintMessageId: messageId || undefined,
    hintPeerSession: peerSession || undefined,
  })
}

/** Pull one-shot dalla propria sessione/coda — usato su focus/chat open, NON in polling. */
export async function syncInboundMessages(): Promise<void> {
  try {
    const session = (await getSessionToken())?.trim()
    if (!session) return

    let pull = await pullSessionBlobs(session)
    const sessionBlobs = Array.isArray(pull?.blobs) ? pull.blobs : []

    if (!sessionBlobs.length && !pull?.canonicalQueueId) {
      let myQueue = (await getInboundQueueId())?.trim()
      if (!myQueue) {
        const ensured = await ensureInboundQueue()
        myQueue = ensured.queueId
        if (ensured.recreated) await notifyPeersOfQueueUpdate(session, myQueue)
      }
      pull = await pullBlobs(myQueue)
      if (pull?.queueMissing) {
        const ensured = await ensureInboundQueue()
        if (ensured.recreated) await notifyPeersOfQueueUpdate(session, ensured.queueId)
        return
      }
    }

    if (pull?.canonicalQueueId) {
      const saved = (await getInboundQueueId())?.trim()
      if (saved !== pull.canonicalQueueId) {
        await saveInboundQueueId(pull.canonicalQueueId)
        watchQueue(pull.canonicalQueueId, session)
        queueToContact.set(pull.canonicalQueueId, '__self__')
      }
    }

    const myQueue = await getInboundQueueId()
    if (myQueue) {
      watchQueue(myQueue, session)
      queueToContact.set(myQueue, '__self__')
    }

    const blobs = Array.isArray(pull?.blobs) ? pull.blobs : []
    for (const blob of blobs) {
      if (typeof blob === 'string' && blob.trim()) {
        await handleIncomingBlob(myQueue ?? '', blob)
      }
    }
  } catch (e) {
    console.error('[ZION] Sync error suppressed:', e)
  }
}

async function purgeContactChat(contactId: string): Promise<void> {
  const contact = await findContact(contactId)
  const hint = contact?.contactId ?? contactId
  const ids = new Set<string>([contactId.trim()])
  if (contact) {
    ids.add(contact.contactId)
    ids.add(contact.sessionToken)
  }
  for (const id of ids) {
    if (id.trim()) await deleteMessagesForContact(id.trim())
  }
  if (contact?.inboundQueueId) {
    unwatchQueue(contact.inboundQueueId)
    queueToContact.delete(contact.inboundQueueId)
  }
  await removeContactCompletely(hint)
  clearUnreadForChat(contactId, contact?.sessionToken)
  if (contact && contact.contactId !== contactId) clearUnread(contact.contactId)
  if (getCurrentChatContactId() === contactId || (contact && getCurrentChatContactId() === contact.contactId)) {
    setCurrentChatContactId('')
    navigate('home')
  }
}

async function purgeContactBySession(fromSessionToken: string): Promise<void> {
  const token = fromSessionToken.trim()
  if (!token) return
  const contact = await findContactBySession(token)
  if (contact) {
    await purgeContactChat(contact.contactId)
    return
  }
  const contacts = await listContacts()
  for (const c of contacts) {
    if (c.sessionToken.trim().toLowerCase() === token.toLowerCase()) {
      await purgeContactChat(c.contactId)
      return
    }
  }
}

export function registerPairedContact(contact: ZionContact): void {
  if (contact.inboundQueueId) {
    watchQueue(contact.inboundQueueId, contact.sessionToken)
    queueToContact.set(contact.inboundQueueId, contact.contactId)
  }
}

export async function acceptIncomingPairingRequest(
  pending: PairingRequest,
): Promise<{ ok: boolean; error?: string; contact?: ZionContact }> {
  const session = (await getSessionToken())?.trim()
  if (!session) return { ok: false, error: 'Sessione non pronta — riapri l’app.' }

  const { queueId } = await ensureInboundQueue()
  const contact = await acceptPendingPairing({
    request: pending,
    mySessionToken: session,
    myPublicKey: await getOutboundPublicKey(),
    myNickname: await getOutboundNickname(),
    myQueueId: queueId,
  })
  if (!contact) {
    return { ok: false, error: 'Accettazione fallita — riprova.' }
  }

  registerPairedContact(contact)
  await setPendingPairingRequest(null)
  setCurrentChatContactId(contact.contactId)
  const { markPromptNewContactRename, scheduleNewContactRenamePrompt } = await import('../ui/modals.ts')
  markPromptNewContactRename(contact.contactId)
  navigate('chat', { contactId: contact.contactId })
  scheduleNewContactRenamePrompt(contact.contactId)
  return { ok: true, contact }
}

export function getCurrentChatContactId(): string {
  return currentChatContactId
}

export function setCurrentChatContactId(id: string): void {
  currentChatContactId = id
  // Notifica SW: sopprimi push banner se già in questa chat
  if (id.trim()) void syncActiveChatToSw(id)
  else clearActiveChatOnSw()
}

export function getCurrentTempChatKey(): string {
  return currentTempChatKey
}

export function setCurrentTempChatKey(key: string): void {
  currentTempChatKey = key.trim()
}

function installLifecycleSync(): void {
  if (lifecycleHooksInstalled) return
  lifecycleHooksInstalled = true
  const onResume = () => {
    try {
      void resumeRealtimeAfterBackground()
    } catch (e) {
      console.error('[ZION] Sync error suppressed:', e)
    }
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onResume()
  })
  window.addEventListener('pageshow', onResume)
  window.addEventListener('focus', onResume)
}

/** Riconnessione socket + sync messaggi dopo giorni in background (Web/PWA/Capacitor). */
export async function resumeRealtimeAfterBackground(): Promise<void> {
  const session = await ensurePrivacySession()
  await ensureSocketReady()
  await syncInboundMessages()
  void syncInbox(session)
}

/** Disabilitato: il polling HTTP /queue/pull generava 404/401 a raffica. Consegna via SSE. */
function startInboundSyncLoop(): void {
  if (syncTimer != null) {
    window.clearInterval(syncTimer)
    syncTimer = null
  }
}

export async function bootstrapRealtime(): Promise<void> {
  clearAllDeliverAckWaiters()
  clearAllOutboundRetries()
  bindTempChatContext({ getCurrentTempChatKey })
  installTempChatHandlers()
  const myNickname = await getBaseNickname()
  const session = await ensurePrivacySession()
  await restorePairedContactsFromMirror()
  const { persistNotifyNamesFromMirror } = await import('../storage/contactsDbForSw.ts')
  const { syncContactNamesToSw } = await import('./activeChatBridge.ts')
  await persistNotifyNamesFromMirror()
  void syncContactNamesToSw()
  const myQueue = await getInboundQueueId()
  if (myQueue) watchQueue(myQueue, session)

  const contacts = await hydrateAllContactsPublicKeys()
  for (const c of contacts) {
    if (c.inboundQueueId) {
      watchQueue(c.inboundQueueId, c.sessionToken)
      queueToContact.set(c.inboundQueueId, c.contactId)
    }
  }

  setBlobHandler((queueId, blob, meta) => {
    void handleIncomingBlob(queueId, blob, {
      hintPeerSession: meta?.peerSessionToken,
    })
  })

  bindSessionHealHandlers({
    sendControl: sendControlToContact,
    reprocessBlob: async (blob, opts) =>
      handleIncomingBlob('', blob, {
        hintPeerSession: opts?.hintPeerSession,
        hintMessageId: opts?.hintMessageId,
      }),
  })

  setMessageEchoHandler((data) => {
    void handleMessageEcho(data)
  })

  setPairingHandlers({
    onRequest: (data) => {
      const nick = String(data.myNickname ?? data.nickname ?? 'Utente').trim()
      void notifyPairingRequest(nick)
      void import('../ui/modals.ts').then(async ({ showIncomingPairRequestModal }) => {
        const { getPendingPairingRequest, setPendingPairingRequest } = await import(
          '../storage/pairingStore.ts'
        )
        showIncomingPairRequestModal({
          nickname: nick,
          onAccept: async () => {
            const pending = await getPendingPairingRequest()
            if (pending) {
              const result = await acceptIncomingPairingRequest(pending)
              if (!result.ok) throw new Error(result.error ?? 'Accettazione fallita')
            } else {
              navigate('home')
            }
          },
          onReject: async () => {
            await setPendingPairingRequest(null)
            navigate('home')
          },
        })
      })
      navigate('home')
    },
    onAccept: (data) => {
      void completePairingFromAccept(data, myNickname).then(async (contact) => {
        registerPairedContact(contact)
        const queueId = await getInboundQueueId()
        if (queueId) await notifyPeersOfQueueUpdate(session, queueId)
        setCurrentChatContactId(contact.contactId)
        const { markPromptNewContactRename, scheduleNewContactRenamePrompt } = await import(
          '../ui/modals.ts'
        )
        markPromptNewContactRename(contact.contactId)
        navigate('chat', { contactId: contact.contactId })
        scheduleNewContactRenamePrompt(contact.contactId)
      })
    },
    onReject: () => navigate('home'),
    onChatDeleted: (data) => {
      const from = String(
        data.fromSessionToken ?? data.mySessionToken ?? data.from ?? '',
      ).trim()
      void purgeContactBySession(from).then(() => navigate('home'))
    },
  })

  installLifecycleSync()

  setHistoryPushHandler((encrypted) => {
    showSyncOverlay('🔄 Sincronizzazione cronologia in corso dal telefono...')
    void applyIncomingHistoryPush(encrypted, (msg) => updateSyncOverlay(msg))
      .then(() => {
        window.setTimeout(() => hideSyncOverlay(), 800)
        const contactId = getCurrentChatContactId()
        if (contactId) navigate('chat', { contactId })
        else navigate('home')
      })
      .catch((err) => {
        updateSyncOverlay(String(err))
        window.setTimeout(() => hideSyncOverlay(), 2500)
      })
  })

  await connectSocket(session)
  await syncMediaPrefs(session, getAutoMediaDownload())
  await syncInboundMessages()
  startInboundSyncLoop()
  void syncPushRegistration(session)
  void ensurePwaPushOnLaunch(session)
  startPushPresenceHeartbeat(session)
  installPushLifecycleHooks(() => getSessionToken())
  void retryAllPendingDecrypts()
  try {
    await ensureFreshDailyCode()
  } catch {
    /* retry on next home open */
  }
  syncAppBadge()
}

async function resolveContactForSend(contactId: string): Promise<ZionContact> {
  const contact = await findContact(contactId)
  if (!contact?.inboundQueueId) throw new Error('Contatto senza coda')
  // Solo chiave locale (pairing / IndexedDB). Nessun fetch /publickey (ZK → 410).
  return ensureContactWithPublicKey(contact, false)
}

/**
 * Consegna blob al peer: SOLO coda HTTP (path reale Android via SSE new_blob + pull).
 * relay/private-blob è legacy (APK Zion non ascolta private_message): fire-and-forget,
 * NON conta come successo (prima marcava consegnato anche se Android non riceveva nulla).
 */
async function deliverBlobToPeer(opts: {
  queueId: string
  blob: string
  toSessionToken: string
  contentKind?: string
  senderSessionToken: string
  messageId: string
  toNickname?: string
  fromNickname?: string
}): Promise<boolean> {
  const toSession = opts.toSessionToken.trim()
  const fromNick = (opts.fromNickname || '').trim()
  if (!opts.queueId.trim() || !toSession) {
    console.warn('[ZION] deliverBlobToPeer: queueId/toSession mancanti')
    return false
  }

  const pushMeta = {
    senderSessionToken: opts.senderSessionToken.trim(),
    messageId: opts.messageId,
    toNickname: (opts.toNickname || '').trim(),
    fromNickname: fromNick,
  }

  const runPushDetailed = async (senderSession: string) => {
    return pushBlobWithResult(
      opts.queueId,
      opts.blob,
      toSession,
      opts.contentKind ?? 'text',
      { ...pushMeta, senderSessionToken: senderSession },
    )
  }

  try {
    await ensureSocketReady()
  } catch {
    /* push ritenta comunque */
  }

  let senderSession = pushMeta.senderSessionToken
  let pushResult = await runPushDetailed(senderSession)

  if (!pushResult.ok && (pushResult.pairingFailed || pushResult.authFailed)) {
    const contact =
      (await findContactBySession(toSession)) ??
      (await listContacts()).find(
        (c) => c.inboundQueueId?.trim() === opts.queueId.trim(),
      )
    if (contact?.publicKey?.trim()) {
      const healed = await silentReHandshakeToContact(contact)
      if (healed) {
        const refreshed = (await getSessionToken())?.trim()
        if (refreshed) senderSession = refreshed
        await ensureSocketReady()
        pushResult = await runPushDetailed(senderSession)
      }
    }
  }

  const pushOk = pushResult.ok

  if (!pushOk) {
    console.warn('[ZION] queue/push fallito — messaggio resta pending, retry background', {
      q: opts.queueId.slice(0, 8),
      to: toSession.slice(0, 8),
    })
  }

  void relayPrivateBlob({
    toSessionToken: toSession,
    fromSessionToken: senderSession,
    blob: opts.blob,
    messageId: opts.messageId,
    toNickname: pushMeta.toNickname,
    fromNickname: fromNick,
  }).catch(() => {})
  return pushOk
}

export async function sendChatMessage(
  contactId: string,
  plainText: string,
  reply?: ReplyTarget,
): Promise<void> {
  const privateKey = await getPrivateKey()
  const session = (await getSessionToken()) ?? ''
  const messageId = await nextMessageId()
  const ttlMs = getMessageTimerTtl(contactId)
  const wireText = wrapWithViewTtl(plainText, ttlMs)

  const buildAndPush = async (): Promise<boolean> => {
    try {
      const live = await resolveContactForSend(contactId)
      const blob = await encryptTextMessage(
        privateKey,
        wireText,
        live.publicKey,
        session,
        messageId,
        reply,
      )
      const sender = await getCurrentNickname()
      return deliverBlobToPeer({
        queueId: live.inboundQueueId,
        blob,
        toSessionToken: live.sessionToken,
        contentKind: 'text',
        senderSessionToken: session,
        messageId,
        toNickname: live.nickname,
        fromNickname: sender,
      })
    } catch (e) {
      console.warn('[ZION] buildAndPush suppressed', e)
      return false
    }
  }

  const msg: ChatMessage = {
    id: messageId,
    contactId,
    text: plainText,
    outgoing: true,
    timestamp: Number(messageId) || Date.now(),
    pending: true,
    replyToId: reply?.replyToId,
    replyPreview: reply?.replyPreview,
    viewTtlMs: ttlMs ?? undefined,
    durationSeconds: ttlMs ? ttlMsToDurationSeconds(ttlMs) : undefined,
  }
  await appendMessage(msg)

  const ok = await buildAndPush()
  if (ok) {
    await patchMessage(messageId, { pending: false })
    armDeliverAckWaiter({ messageId, contactId })
    return
  }

  // Niente throw/alert: resta in chat come pending e ritenta in background
  scheduleOutboundRetry({
    messageId,
    contactId,
    retryFn: buildAndPush,
  })
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

export async function sendMediaMessage(
  contactId: string,
  file: File,
  caption = '',
  onStatus?: (status: string) => void,
): Promise<void> {
  const contact = await resolveContactForSend(contactId)

  const raw = new Uint8Array(await file.arrayBuffer())
  const mimeHint = file.type || 'application/octet-stream'
  const looksGif =
    isGifBytes(raw) ||
    mimeHint.toLowerCase() === 'image/gif' ||
    /\.gif$/i.test(file.name)
  const sanitized = await sanitizeMediaFile(raw, mimeHint, file.name)
  if (!sanitized) {
    const isDoc =
      kindFromMime(mimeHint) === KIND_FILE ||
      /\.(pdf|docx?|txt|zip|rar|xlsx|pptx)$/i.test(file.name)
    if (looksGif && raw.length > MAX_GIF_BYTES) {
      throw new Error(GIF_TOO_LARGE_MSG)
    }
    if (isDoc && raw.length > MAX_DOCUMENT_BYTES) {
      throw new Error(DOCUMENT_TOO_LARGE_MSG)
    }
    throw new Error(
      looksGif
        ? GIF_TOO_LARGE_MSG
        : isDoc
          ? 'Documento non supportato o troppo grande (max 5 MB).'
          : 'Immagine/sticker non supportato o troppo grande (max 3 MB). Su iPhone prova da File o Libreria Foto.',
    )
  }

  const privateKey = await getPrivateKey()
  const session = (await getSessionToken()) ?? ''
  const messageId = await nextMessageId()
  const origSize = sanitized.bytes.length
  const trimmedCaption = caption.trim()
  const displayText =
    sanitized.kind === KIND_FILE && !trimmedCaption && sanitized.fileName
      ? documentDisplayLabel(sanitized.fileName, origSize)
      : trimmedCaption

  const processingLabel = '[COMPRESSING/ENCRYPTING...]'
  const isDocument = sanitized.kind === KIND_FILE

  if (isDocument) {
    await appendMessage({
      id: messageId,
      contactId,
      text: displayText,
      outgoing: true,
      timestamp: Number(messageId) || Date.now(),
      pending: true,
      mediaKind: sanitized.kind,
      mediaMime: sanitized.mime,
      mediaFileName: sanitized.fileName,
      mediaOrigSize: origSize,
      processingStatus: processingLabel,
    })
    onStatus?.(processingLabel)
  }

  let wireBytes = sanitized.bytes
  let gzipped = false

  try {
    if (isDocument) {
      const packed = await gzipCompressIfSmaller(sanitized.bytes)
      wireBytes = packed.bytes
      gzipped = packed.gzipped
    }

    const mediaB64 = bytesToBase64(wireBytes)
    const ttlMs = getMessageTimerTtl(contactId)
    const wireCaption = wrapWithViewTtl(trimmedCaption, ttlMs)

    const blob = await encryptMediaMessage(
      privateKey,
      wireCaption,
      contact.publicKey,
      {
        kind: sanitized.kind,
        mime: sanitized.mime,
        mediaB64,
        fileName: sanitized.fileName,
        mediaGzip: gzipped || undefined,
        mediaOrigSize: isDocument ? origSize : undefined,
      },
      session,
      messageId,
    )

    const msg: ChatMessage = {
      id: messageId,
      contactId,
      text: displayText,
      outgoing: true,
      timestamp: Number(messageId) || Date.now(),
      pending: true,
      mediaKind: sanitized.kind,
      mediaMime: sanitized.mime,
      mediaFileName: sanitized.fileName,
      mediaGzip: gzipped || undefined,
      mediaOrigSize: isDocument ? origSize : undefined,
      viewTtlMs: ttlMs ?? undefined,
      durationSeconds: ttlMs ? ttlMsToDurationSeconds(ttlMs) : undefined,
    }

    if (!isDocument) {
      await appendMessage(msg)
    } else {
      await patchMessage(messageId, {
        mediaGzip: gzipped || undefined,
        processingStatus: undefined,
      })
    }

    await putMediaBytes(contactId, messageId, wireBytes, sanitized.mime)

    const sender = await getCurrentNickname()
    const ok = await deliverBlobToPeer({
      queueId: contact.inboundQueueId,
      blob,
      toSessionToken: contact.sessionToken,
      contentKind: sanitized.kind,
      senderSessionToken: session,
      messageId,
      toNickname: contact.nickname,
      fromNickname: sender,
    })
    if (!ok) {
      scheduleOutboundRetry({
        messageId,
        contactId,
        retryFn: async () =>
          deliverBlobToPeer({
            queueId: contact.inboundQueueId,
            blob,
            toSessionToken: contact.sessionToken,
            contentKind: sanitized.kind,
            senderSessionToken: session,
            messageId,
            toNickname: contact.nickname,
            fromNickname: sender,
          }),
      })
      await patchMessage(messageId, { processingStatus: undefined })
      return
    }

    await patchMessage(messageId, { processingStatus: undefined, pending: false })
    armDeliverAckWaiter({ messageId, contactId })
  } catch (err) {
    if (isDocument) await deleteMessage(messageId)
    throw err
  }
}

export async function sendVoiceMessage(
  contactId: string,
  raw: Uint8Array,
  mime: string,
  durationMsHint = 0,
): Promise<void> {
  const contact = await resolveContactForSend(contactId)

  // WebM → WAV PCM 16-bit mono; se fallisce / <100B → fallback WebM originale
  const encoded = await encodeVoiceForAndroid(raw, mime, durationMsHint)
  const sanitized = sanitizeAudioBytes(encoded.bytes, encoded.mime)
  if (!sanitized) throw new Error('Vocale non valido o troppo grande (max 3 MB).')

  const wireMime = sanitized.mime.trim() || 'audio/webm'
  const envelopeDurationMs = Math.round(
    durationMsHint > 0 ? durationMsHint : encoded.durationMs,
  )
  const durationMs = envelopeDurationMs
  if (durationMs <= 0) {
    throw new Error('Durata vocale sconosciuta — riprova a registrare.')
  }

  const privateKey = await getPrivateKey()
  const session = (await getSessionToken()) ?? ''
  const messageId = await nextMessageId()
  const ttlMs = getMessageTimerTtl(contactId)
  const wireCaption = wrapWithViewTtl('', ttlMs)
  const mediaB64 = bytesToBase64(sanitized.bytes)

  // durationMs forzato nell'envelope cifrato (intero ms dal MediaRecorder)
  const headerHex = Array.from(sanitized.bytes.subarray(0, 12))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ')
  console.info(
    '[ZION voice] send durationMs=',
    durationMs,
    'mime=',
    wireMime,
    'bytes=',
    sanitized.bytes.length,
    'header12=',
    headerHex,
  )
  const blob = await encryptMediaMessage(
    privateKey,
    wireCaption,
    contact.publicKey,
    {
      kind: KIND_AUDIO,
      mime: wireMime,
      mediaB64,
      durationMs: envelopeDurationMs,
    },
    session,
    messageId,
  )

  const msg: ChatMessage = {
    id: messageId,
    contactId,
    text: '',
    outgoing: true,
    timestamp: resolveMessageTimestamp({ messageId }),
    pending: true,
    mediaKind: KIND_AUDIO,
    mediaMime: wireMime,
    audioDurationMs: durationMs,
    viewTtlMs: ttlMs ?? undefined,
    durationSeconds: ttlMs ? ttlMsToDurationSeconds(ttlMs) : undefined,
  }
  await appendMessage(msg)
  await putMediaBytes(contactId, messageId, sanitized.bytes, wireMime)

  const sender = await getCurrentNickname()
  const ok = await deliverBlobToPeer({
    queueId: contact.inboundQueueId,
    blob,
    toSessionToken: contact.sessionToken,
    contentKind: KIND_AUDIO,
    senderSessionToken: session,
    messageId,
    toNickname: contact.nickname,
    fromNickname: sender,
  })
  if (!ok) {
    scheduleOutboundRetry({
      messageId,
      contactId,
      retryFn: async () =>
        deliverBlobToPeer({
          queueId: contact.inboundQueueId,
          blob,
          toSessionToken: contact.sessionToken,
          contentKind: KIND_AUDIO,
          senderSessionToken: session,
          messageId,
          toNickname: contact.nickname,
          fromNickname: sender,
        }),
    })
    return
  }
  await patchMessage(messageId, { pending: false })
  armDeliverAckWaiter({ messageId, contactId })
}

async function sendControlToContact(contactId: string, controlPlain: string): Promise<boolean> {
  try {
    const contact = await resolveContactForSend(contactId)
    const privateKey = await getPrivateKey()
    const session = (await getSessionToken()) ?? ''
    const messageId = await nextMessageId()
    const blob = await encryptControlMessage(
      privateKey,
      controlPlain,
      contact.publicKey,
      session,
      messageId,
    )
    const fromNick = await getCurrentNickname()
    const ok = await deliverBlobToPeer({
      queueId: contact.inboundQueueId,
      blob,
      toSessionToken: contact.sessionToken,
      contentKind: 'text',
      senderSessionToken: session,
      messageId,
      toNickname: contact.nickname,
      fromNickname: fromNick,
    })
    // ACK / KEY_OFFER / RESYNC: mai wake, mai impatto sul messaggio utente
    const quiet =
      parseMsgDeliverAck(controlPlain) != null ||
      parseMsgResyncRequest(controlPlain) != null ||
      parseMsgKeyOffer(controlPlain) != null ||
      parseMsgReadAck(controlPlain) != null
    if (ok && !quiet) {
      void wakePeerBySession(contact.sessionToken, buildNativeApnsPayload('ZION', 'Aggiornamento chat'))
    }
    return ok
  } catch (e) {
    console.warn('[ZION] sendControl quiet fail (ignorato)', e)
    return false
  }
}

/** Destinatario: avvia countdown effimero quando il messaggio è in chat aperta. */
export async function armEphemeralCountdown(messageId: string): Promise<void> {
  const msg = await getMessageById(messageId)
  if (!msg || msg.outgoing) return
  const dur = msg.durationSeconds ?? 0
  if (dur <= 0) return
  if (msg.countdownStartedAt && msg.countdownStartedAt > 0) return
  await patchMessage(messageId, { countdownStartedAt: Date.now() })
}

/** Destinatario: countdown a zero → ACK al mittente + wipe locale. */
export async function onEphemeralCountdownExpired(messageId: string): Promise<void> {
  const msg = await getMessageById(messageId)
  if (!msg || msg.outgoing) return
  const dur = msg.durationSeconds ?? 0
  if (dur <= 0) return
  if (!(msg.countdownStartedAt && msg.countdownStartedAt > 0)) {
    await armEphemeralCountdown(messageId)
    return
  }
  const remaining = ephemeralRemainingSeconds(msg)
  if (remaining > 0) return

  void sendControlToContact(msg.contactId, buildMsgReadAck(messageId))
  await deleteMediaBytes(msg.contactId, messageId)
  await deleteMessage(messageId)
}

/** Mittente: peer ha inviato MSG_READ_ACK → mostra busta / svuota testo. */
export async function applyIncomingMsgReadAck(contactId: string, messageId: string): Promise<void> {
  const msg = await getMessageById(messageId)
  if (!msg || !msg.outgoing) {
    // id può non coincidere se normalizzato diversamente — cerca tra outgoing del contatto
    const all = await loadMessages(contactId)
    const found = all.find((m) => m.outgoing && m.id === messageId)
    if (!found) return
    await patchMessage(found.id, {
      readAndDestroyed: true,
      text: '',
      mediaKind: undefined,
      mediaMime: undefined,
      mediaFileName: undefined,
    })
    await deleteMediaBytes(contactId, found.id)
    return
  }
  if (msg.readAndDestroyed) return
  await patchMessage(messageId, {
    readAndDestroyed: true,
    text: '',
    mediaKind: undefined,
    mediaMime: undefined,
    mediaFileName: undefined,
  })
  await deleteMediaBytes(contactId, messageId)
}

export function ephemeralRemainingSeconds(msg: ChatMessage, now = Date.now()): number {
  const dur = msg.durationSeconds ?? 0
  if (dur <= 0) return 0
  const started = msg.countdownStartedAt ?? 0
  if (started <= 0) return dur
  const elapsed = Math.floor((now - started) / 1000)
  return Math.max(0, dur - elapsed)
}

export function isEphemeralMessage(msg: ChatMessage): boolean {
  return (msg.durationSeconds ?? 0) > 0
}

/** Elimina messaggio solo in locale. */
export async function deleteMessageForMe(messageId: string): Promise<void> {
  await deleteMessage(messageId)
}

/** Elimina messaggio per tutti (solo i tuoi messaggi inviano NYXIA al peer). */
export async function deleteMessageForEveryone(messageId: string): Promise<{ ok: boolean; message: string }> {
  const msg = await getMessageById(messageId)
  if (!msg) return { ok: false, message: 'Messaggio non trovato.' }
  if (msg.outgoing) {
    const sent = await sendControlToContact(msg.contactId, buildMessageDeleteNotify(messageId))
    if (!sent) return { ok: false, message: 'Invio eliminazione al peer fallito.' }
  }
  await deleteMessage(messageId)
  return { ok: true, message: msg.outgoing ? 'Eliminato per tutti.' : 'Eliminato solo per te.' }
}

/** Svuota chat in locale e rimuove il contatto dalla Home. */
export async function deleteChatForMe(contactId: string): Promise<void> {
  await purgeContactChat(contactId)
}

/** Svuota chat locale + notifica peer (evento pairing + CHAT_DEL + delete singoli). */
export async function deleteChatForEveryone(contactId: string): Promise<{ ok: boolean; message: string }> {
  const contact = await findContact(contactId)
  if (!contact) return { ok: false, message: 'Contatto non trovato.' }

  const session = (await getSessionToken()) ?? ''
  let fail = 0

  if (contact.sessionToken && session) {
    const sent = await sendChatDeleted(contact.sessionToken, session)
    if (!sent) fail++
  }

  if (!(await sendControlToContact(contactId, buildChatClearNotify()))) fail++

  const msgs = await loadMessages(contactId)
  for (const m of msgs) {
    if (!m.outgoing) continue
    const sent = await sendControlToContact(contactId, buildMessageDeleteNotify(m.id))
    if (!sent) fail++
  }

  await purgeContactChat(contactId)

  if (contact.sessionToken) {
    void wakePeerBySession(contact.sessionToken, buildNativeApnsPayload('ZION', 'Chat eliminata'))
  }

  if (fail > 0) {
    return {
      ok: true,
      message: `Chat eliminata localmente. ${fail} notifiche al peer non inviate — riapri l'app Android.`,
    }
  }
  return { ok: true, message: 'Chat eliminata per tutti.' }
}

export async function pairFromZnOrPayload(raw: string): Promise<{ ok: boolean; message: string }> {
  const trimmed = raw.trim()
  if (!trimmed) return { ok: false, message: 'Inserisci un codice o payload.' }

  const { parsePairingPayload } = await import('../pairing/pairingPayload.ts')
  const { resolveDailyCode, sendPairRequest } = await import('../socket2/ZionSocketClient.ts')

  let toSession = ''
  if (trimmed.replace(/\s+/g, '').startsWith('ZION1::')) {
    const data = parsePairingPayload(trimmed)
    if (!data) return { ok: false, message: 'QR scaduto o non valido.' }
    toSession = data.sessionToken
  } else {
    const code = trimmed.toUpperCase()
    if (!/^ZN-\d{6}$/.test(code)) return { ok: false, message: 'Formato: ZN-123456 o QR ZION1::' }
    const resolved = await resolveDailyCode(code)
    if (!resolved) return { ok: false, message: 'Codice ZN- non valido. Riapri ZION sul dispositivo che lo ha generato e copia il codice aggiornato.' }
    toSession = resolved.sessionToken
  }

  let session = await ensurePrivacySession()
  await connectSocket(session)
  let { queueId } = await ensureInboundQueue()

  await ensureKeyPair()
  const pairPayload = {
    toSessionToken: toSession,
    mySessionToken: session,
    myPublicKey: await getOutboundPublicKey(),
    myNickname: await getOutboundNickname(),
    myQueueId: queueId,
  }

  let result = await sendPairRequest(pairPayload)
  if (!result.ok) {
    // Non remintare la sessione: ricrea solo la coda inbound se necessario.
    await saveInboundQueueId('')
    await saveQueueOwnerPubKey('')
    ;({ queueId } = await ensureInboundQueue())
    await connectSocket(session)
    result = await sendPairRequest({
      ...pairPayload,
      mySessionToken: session,
      myQueueId: queueId,
      myPublicKey: await getOutboundPublicKey(),
      myNickname: await getOutboundNickname(),
    })
  }

  if (result.ok) {
    const nick = await getOutboundNickname()
    void wakePeerBySession(toSession, buildNativeApnsPayload(nick || 'ZION', 'Richiesta di accoppiamento'))
    void pollInboxBurst(session, 6, 2000)
  }
  return result.ok
    ? { ok: true, message: 'Richiesta di accoppiamento inviata.' }
    : { ok: false, message: pairSendErrorMessage(result.error) }
}

function pairSendErrorMessage(code?: string): string {
  switch (code) {
    case 'invalid_sender_session':
      return 'Sessione scaduta sul server. Ricarica la pagina e riprova.'
    case 'invalid_signature':
      return 'Errore di autenticazione. Ricarica la pagina (Ctrl+F5) e riprova.'
    case 'timestamp_expired':
      return 'Ora del dispositivo non corretta. Imposta data/ora automatica e riprova.'
    case 'rate_limited':
      return 'Troppe richieste. Attendi un minuto e riprova.'
    case 'missing_fields':
      return 'Richiesta incompleta. Ricarica la pagina e riprova.'
    default:
      return 'Invio fallito. Ricarica la pagina e riprova.'
  }
}

export async function getOrCreatePairingPayload(): Promise<{ payload: string; exp: number }> {
  const { buildPairingPayload } = await import('../pairing/pairingPayload.ts')
  const { loadPairCache, savePairCache } = await import('../pairing/pairingPayloadStore.ts')
  const queueId = (await getInboundQueueId())?.trim() ?? ''
  const cached = loadPairCache()
  if (cached && cached.queueId === queueId && Date.now() < cached.exp) {
    return { payload: cached.payload, exp: cached.exp }
  }
  const built = await buildPairingPayload()
  savePairCache({ payload: built.payload, exp: built.exp, queueId })
  return built
}

export async function regeneratePairingPayload(): Promise<{ payload: string; exp: number }> {
  const { clearPairCache } = await import('../pairing/pairingPayloadStore.ts')
  clearPairCache()
  return getOrCreatePairingPayload()
}

export async function getMyShareCode(): Promise<string> {
  const savedCode = await getDailyCode()
  if (savedCode) {
    try {
      return await ensureFreshDailyCode()
    } catch {
      return savedCode
    }
  }
  await ensurePrivacySession()
  try {
    return await ensureFreshDailyCode()
  } catch {
    return (await getDailyCode()) ?? '—'
  }
}

export function labelForContact(c: ZionContact): string {
  return contactDisplayName(c)
}

/** Nickname base per pairing (senza suffisso giornaliero) — come Android getBaseNickname(). */
export async function getOutboundNickname(): Promise<string> {
  const base = await getBaseNickname()
  return base || 'zion-user'
}

export async function getOutboundPublicKey(): Promise<string> {
  return getPublicKeyBase64()
}

/** Nickname visibile con suffisso giornaliero UTC. */
export async function getDisplayNickname(): Promise<string> {
  return (await getCurrentNickname()) || '—'
}

export {
  acceptTempChatInvite,
  cancelTempChatOutgoing,
  endTempChatEarly,
  formatTempRemaining,
  getTempChatByKey,
  listPendingIncomingTempChats,
  listTempChats,
  rejectTempChatInvite,
  startTempChatWithContact,
}
